import { useTranslation } from 'react-i18next'

import type { AuthnetConfig } from '#/shared/config/authorize-net'
import { cn } from '#/shared/lib/utils'
import { Input } from '#/shared/ui/input'
import { Label } from '#/shared/ui/label'

import type { UseAddCardArgs } from '../model/add-card-shared'
import { useAddCardAuthnet } from '../model/use-add-card-authnet'
import type { UseAddCardAuthnetResult } from '../model/use-add-card-authnet'

import { AddCardBody, AddCardFooter } from './add-card-form-fields'

const CARD_NUMBER_MAX = 23
const CVC_MAX = 4

function formatCardNumber(value: string): string {
  return value
    .replace(/\D/g, '')
    .slice(0, 19)
    .replace(/(.{4})/g, '$1 ')
    .trim()
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4)
  return digits.length > 2
    ? `${digits.slice(0, 2)} / ${digits.slice(2)}`
    : digits
}

function AuthnetCardFields({ add }: { add: UseAddCardAuthnetResult }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <Label size="sm">{t('addCard.authnet.cardNumber')}</Label>
        <Input
          inputSize="sm"
          inputMode="numeric"
          autoComplete="cc-number"
          maxLength={CARD_NUMBER_MAX}
          placeholder={t('addCard.authnet.cardNumberPlaceholder')}
          className={cn(
            'h-10 rounded-md px-2.5',
            add.cardError && 'border-red-500',
          )}
          value={add.card.number}
          onChange={(e) =>
            add.setCardField('number', formatCardNumber(e.target.value))
          }
        />
      </div>
      <div className="flex w-full items-start gap-3">
        <div className="flex flex-1 flex-col gap-0.5">
          <Label size="sm">{t('addCard.authnet.expiry')}</Label>
          <Input
            inputSize="sm"
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder={t('addCard.authnet.expiryPlaceholder')}
            className="h-10 rounded-md px-2.5"
            value={add.card.expiry}
            onChange={(e) =>
              add.setCardField('expiry', formatExpiry(e.target.value))
            }
          />
        </div>
        <div className="flex flex-1 flex-col gap-0.5">
          <Label size="sm">{t('addCard.authnet.cvc')}</Label>
          <Input
            inputSize="sm"
            inputMode="numeric"
            autoComplete="cc-csc"
            maxLength={CVC_MAX}
            placeholder={t('addCard.authnet.cvcPlaceholder')}
            className="h-10 rounded-md px-2.5"
            value={add.card.cvc}
            onChange={(e) =>
              add.setCardField('cvc', e.target.value.replace(/\D/g, ''))
            }
          />
        </div>
      </div>
      {add.cardError && <p className="text-xs text-red-500">{add.cardError}</p>}
    </div>
  )
}

export function AddCardAuthnetPanel({
  args,
  authnet,
  onBack,
}: {
  args: UseAddCardArgs
  authnet: AuthnetConfig
  onBack: () => void
}) {
  const add = useAddCardAuthnet(args, authnet)
  return (
    <>
      <AddCardBody fields={add} cardField={<AuthnetCardFields add={add} />} />
      <AddCardFooter
        onCancel={onBack}
        onPrimary={add.submit}
        canSubmit={add.canSubmit}
        isSubmitting={add.isSubmitting}
      />
    </>
  )
}
